import { Injectable, computed, signal } from '@angular/core';
import { Property, Unit } from '../models';
import { MOCK_PROPERTIES } from '../mocks/properties.mock';

@Injectable({ providedIn: 'root' })
export class PropertiesService {
  private readonly _properties = signal<Property[]>(MOCK_PROPERTIES);
  private readonly _loading = signal(false);
  private readonly _selectedPropertyId = signal<number | null>(null);
  private readonly _selectedUnitId = signal<string | null>(null);

  readonly properties = this._properties.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly selectedPropertyId = this._selectedPropertyId.asReadonly();
  readonly selectedUnitId = this._selectedUnitId.asReadonly();

  readonly count = computed(() => this._properties().length);

  readonly selectedProperty = computed(() => {
    const id = this._selectedPropertyId();
    if (id === null) return null;
    return this._properties().find(p => p.id === id) ?? null;
  });

  readonly selectedUnit = computed((): Unit | null => {
    const property = this.selectedProperty();
    if (!property) return null;

    const unitId = this._selectedUnitId();
    if (unitId === null) return property.units[0] ?? null;

    return property.units.find(u => u.id === unitId) ?? property.units[0] ?? null;
  });

  getPropertyById(id: number): Property | undefined {
    return this._properties().find(p => p.id === id);
  }

  getLowestPrice(property: Property): number {
    return Math.min(...property.units.map(u => u.price));
  }

  getHighestPrice(property: Property): number {
    return Math.max(...property.units.map(u => u.price));
  }

  getPriceRange(property: Property): string {
    const min = this.getLowestPrice(property);
    const max = this.getHighestPrice(property);
    if (min === max) return `${min} zł`;
    return `${min} - ${max} zł`;
  }

  selectProperty(id: number | null): void {
    this._selectedPropertyId.set(id);
    this._selectedUnitId.set(null);
  }

  selectUnit(unitId: string | null): void {
    this._selectedUnitId.set(unitId);
  }

  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }
}
